// src/features/files/pages/FileViewerPage.jsx
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Box, CircularProgress, Alert } from '@mui/material';
import { FileFullscreenViewer } from '../components/FileFullscreenViewer';
import { apiClient } from '../../../services/api/client';

/**
 * FileViewerPage - Page for viewing files in fullscreen mode
 * Can be accessed directly via URL: /files/view/:fileId
 * Fetches file metadata and displays fullscreen viewer
 */
export default function FileViewerPage() {
  const { fileId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const versionParam = searchParams.get('version');
  const versionNumber = versionParam ? parseInt(versionParam, 10) : null;
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchFile = async () => {
      if (!fileId) {
        setError('No file ID provided');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        // Fetch file metadata
        const response = await apiClient.get(`/api/v1/file/files/${fileId}`);

        if (response.data?.success && response.data?.data) {
          setFile(response.data.data);
        } else {
          setError('File not found');
        }
      } catch (err) {
        setError(err.message || 'Failed to load file');
        console.error('Error fetching file:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchFile();
  }, [fileId]);

  const handleClose = () => {
    // Check if this is a standalone window (opened in a new tab)
    if (window.history.length === 1) {
      // Close the tab if it was opened from a link
      window.close();
    } else {
      // Otherwise navigate back
      navigate(-1);
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          width: '100vw',
          height: '100vh',
          zIndex: 1300,
          backgroundColor: '#000',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <CircularProgress sx={{ color: '#fff' }} />
      </Box>
    );
  }

  if (error || !file) {
    return (
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          width: '100vw',
          height: '100vh',
          zIndex: 1300,
          backgroundColor: '#000',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          p: 2,
        }}
      >
        <Alert
          severity="error"
          sx={{
            maxWidth: 500,
            '& .MuiAlert-message': {
              color: '#000',
            },
          }}
        >
          {error || 'Failed to load file'}
        </Alert>
      </Box>
    );
  }

  return (
    <FileFullscreenViewer
      fileId={fileId}
      filename={file.filename}
      filetype={file.filetype}
      versionNumber={versionNumber}
      onClose={handleClose}
    />
  );
}
